import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TablePagination,
    TableRow,
} from "@mui/material"
import { ReactNode, useEffect, useRef, useState } from "react"
import { Order } from "../../models/SortOrder"
import {
    GetTableRowId,
    HandleChange,
    HandleDeleteRow,
    HandlePaste,
    HandleSelect,
    TableColumn,
    UpdateTableRows,
} from "../../models/Table"
import {
    TableRowOptions,
    addTableRow,
    getComparator,
    getSelectedRowsWithShift,
    handleTextfieldPaste,
    isRowInserted,
} from "../../utils/TableUtils"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { NexTableHeader } from "./NexTableHeader"
import { NexTableRow } from "./NexTableRow"
import { NexTableToolbar } from "./NexTableToolbar"

interface NexTableProps<T extends object> {
    label: string
    rows: T[]
    primaryKey: keyof T
    tableRowOptions?: TableRowOptions<T>
    stickyColumns?: TableColumn<T>[]
    columns: TableColumn<T>[]
    toolbarContents?: ReactNode
    updateTableRows: UpdateTableRows<T>
    canDeleteSelected?: boolean
    canAddRow?: boolean
    canSelect?: boolean
    disablePagination?: boolean
    disableSelectAll?: boolean
    selectedRowOverlay?: (row: T) => ReactNode
    footerText?: string
    pasteColumns?: (keyof T)[]
    maxHeight?: number
    selectedRows?: {
        selectedIds: number[]
        setSelectedIds: (selected: number[]) => void
    }
}

export const NexTable = <T extends object>(props: NexTableProps<T>) => {
    const {
        label,
        rows,
        primaryKey,
        tableRowOptions,
        stickyColumns,
        columns,
        toolbarContents,
        updateTableRows,
        canAddRow = false,
        canDeleteSelected = false,
        canSelect = false,
        disablePagination = false,
        disableSelectAll = false,
        selectedRowOverlay,
        footerText,
        maxHeight,
        pasteColumns = [],
        selectedRows,
    } = props
    const scrollRef = useRef<HTMLDivElement>(null)

    // Use these only if selectedRows is undefined
    const [innerSelectedIds, setInnerSelectedIds] = useState<number[]>(selectedRows?.selectedIds ?? [])
    const selectedIds = selectedRows?.selectedIds ?? innerSelectedIds
    const setSelectedIds = selectedRows?.setSelectedIds ?? setInnerSelectedIds

    const [lastSelected, setLastSelected] = useState<number>(-1)
    const [page, setPage] = useState<number>(0)
    const [rowsPerPage, setRowsPerPage] = useState<number>(10)

    const sortOrderMap = useLocalStorageStore((state) => state.tableSortOrders)
    const { orderBy, order } = sortOrderMap[label] ?? { orderBy: "", order: Order.asc }

    const getRowId: GetTableRowId<T> = (row) => {
        return row[primaryKey] as unknown as number
    }

    const getIndexOfRow = (rowId: number) => rows.findIndex((row) => getRowId(row) === rowId)

    let allSelectable = rows
    if (tableRowOptions?.canSelectRow != null) {
        allSelectable = rows.filter((row) =>
            tableRowOptions?.canSelectRow == null ? true : tableRowOptions.canSelectRow(row),
        )
    }

    // Update rows on new order
    useEffect(() => {
        const orderedRows = rows.slice().sort(getComparator(order, orderBy as keyof T))
        updateTableRows(orderedRows)
    }, [order, orderBy, rows.length !== 0])

    const resetTableScroll = () => {
        if (scrollRef?.current) {
            scrollRef.current.scrollTop = 0
        }
    }

    const handleChangePage = (newPage: number) => {
        setPage(newPage)
        resetTableScroll()
    }

    const handleChangeRowsPerPage = (value: number) => {
        setRowsPerPage(value)
        setPage(0)
        resetTableScroll()
    }

    const handleChange: HandleChange<any, T> = (value, columnId, rowId) => {
        const index = getIndexOfRow(rowId)
        const currentRow = rows[index]
        const updatedRow = { ...currentRow, [columnId]: value || null }
        updateTableRows([...rows.slice(0, index), updatedRow, ...rows.slice(index + 1)], [updatedRow])
    }

    const handleDeleteRow: HandleDeleteRow = (rowId) => {
        const index = rows.findIndex((row) => getRowId(row) === rowId)
        const updatedRows = rows.slice()
        updatedRows.splice(index, 1)

        updateTableRows(updatedRows)
    }

    const addRow = () => {
        const rowModel = columns.map((col) => col.id)

        const newRow = rowModel.reduce((row, key) => {
            // @ts-ignore
            row[key] = undefined

            return row
        }, {} as T)
        const addedRows = rows.filter((row) => isRowInserted(row[primaryKey]))
        const row = addTableRow(newRow, addedRows.length, primaryKey)

        updateTableRows([row, ...rows], undefined, row)
    }

    const deleteSelected = () => {
        const { updatedRows, rowsDeleted } = rows.reduce(
            (acc: { updatedRows: T[]; rowsDeleted: T[] }, row) => {
                const selected = selectedIds.includes(getRowId(row))

                const updated = [...acc.updatedRows]
                const deleted = [...acc.rowsDeleted]
                if (selected) {
                    deleted.push(row)
                } else {
                    updated.push(row)
                }

                return {
                    updatedRows: updated,
                    rowsDeleted: deleted,
                }
            },
            { updatedRows: [], rowsDeleted: [] },
        )
        setSelectedIds([])
        const newRowCount = rows.length - rowsDeleted.length
        let maxSelectedPage = Math.ceil(newRowCount / rowsPerPage) - 1
        if (maxSelectedPage < 0) {
            maxSelectedPage = 0
        }
        if (page > maxSelectedPage) {
            setPage(maxSelectedPage)
        }
        updateTableRows(updatedRows, rowsDeleted)
    }

    // Checkbox selecting if canSelect is enabled
    const handleSelect: HandleSelect = (shiftClick, rowId) => {
        let newSelectedIds: number[] = []

        // Select/Unselect Rows
        if (rowId != null) {
            // adds or removes specific rowId
            if (shiftClick) {
                // get all rows between the last row clicked and current clicked row
                const lastSelectedRowIndex = getIndexOfRow(lastSelected)
                const rowIndex = getIndexOfRow(rowId)

                const rowsFromShiftClick = getSelectedRowsWithShift(lastSelectedRowIndex, rowIndex, rows)
                const idsFromShiftClickRows = rowsFromShiftClick.map((row) => getRowId(row))
                if (selectedIds.includes(rowId)) {
                    // deselect all
                    newSelectedIds = selectedIds.filter((id) => !idsFromShiftClickRows.includes(id))
                } else {
                    // select all
                    newSelectedIds = [...new Set([...selectedIds, ...idsFromShiftClickRows])]
                }
            } else {
                // not shift click

                if (selectedIds.includes(rowId)) {
                    // Is selected - deselect
                    newSelectedIds = selectedIds.filter((id) => id !== rowId)
                } else {
                    newSelectedIds = [...selectedIds, rowId]
                }
            }
            setLastSelected(rowId)
        } else {
            // Select/unselect All
            if (!selectedIds.length) {
                // Select all
                newSelectedIds = allSelectable.map((row) => getRowId(row))
            }
            setLastSelected(-1)
        }
        setSelectedIds(newSelectedIds)
    }

    const handlePaste: HandlePaste<T> = (event, rowId, colId: keyof T) => {
        const startIndex = getIndexOfRow(rowId)
        if (pasteColumns.includes(colId)) {
            const [allRows, updatedRows] = handleTextfieldPaste<T>(event, startIndex, colId, rows)
            updateTableRows(allRows, updatedRows)
        }
    }

    const slicedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    const headerAndRowProps = {
        columns,
        stickyColumns,
        handleSelect,
        getRowId,
        selectedIds,
    }
    return (
        <Paper sx={{ mb: 4 }}>
            <NexTableToolbar
                selectedIds={selectedIds}
                label={label}
                onDeleteSelectedRows={canDeleteSelected ? deleteSelected : undefined}
                addRow={canAddRow && addRow}
            >
                <Box display={"flex"} gap={2} alignItems={"center"} justifyContent={"center"}>
                    {toolbarContents}
                </Box>
            </NexTableToolbar>
            <TableContainer ref={scrollRef} sx={{ maxHeight: maxHeight ?? 1200, minHeight: 200, overflowY: "auto" }}>
                <Table stickyHeader size="small">
                    <NexTableHeader
                        tableLabel={label}
                        disableSelectAll={disableSelectAll}
                        numSelected={selectedIds.length}
                        numSelectable={allSelectable.length}
                        rowOverlay={selectedRowOverlay != null}
                        canSelect={canSelect && !!allSelectable.length}
                        {...headerAndRowProps}
                    />
                    <TableBody>
                        {slicedRows.map((row, idx) => {
                            const selected = selectedIds.includes(getRowId(row))
                            return (
                                <NexTableRow
                                    key={idx}
                                    row={row}
                                    selected={selected}
                                    rowOverlay={selectedRowOverlay}
                                    canSelectRow={tableRowOptions?.canSelectRow}
                                    handleDeleteRow={handleDeleteRow}
                                    handlePaste={handlePaste}
                                    handleChange={handleChange}
                                    {...headerAndRowProps}
                                />
                            )
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            {!disablePagination && (
                <TableContainer>
                    <Table>
                        <TableFooter>
                            <TableRow>
                                {footerText && <TableCell>{footerText}</TableCell>}
                                <TablePagination
                                    sx={{ overflow: "initial" }}
                                    rowsPerPageOptions={[10, 20, 50, 100]}
                                    count={rows.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(e, page) => handleChangePage(page)}
                                    onRowsPerPageChange={(e) => handleChangeRowsPerPage(+e.target.value)}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    )
}
